import {useState} from 'react';
import {t} from 'i18next';
import ArrModal from '../../../common/arr-modal/ArrModal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {ReactComponent as EndToEndIcon} from '../../../../../src/assets/images/end-to-end.svg';
import {ReactComponent as ReviewRateTrendIcon} from '../../../../../src/assets/images/review_rate_trend.svg';
import {ReactComponent as ReviewProductivityIcon} from '../../../../../src/assets/images/review-productivity.svg';
import {ReactComponent as ReviewProgressIcon} from '../../../../../src/assets/images/review-progress.svg';
import {ReactComponent as TaggingIcon} from '../../../../../src/assets/images/tagging.svg';
import {ReactComponent as TotalReviewIcon} from '../../../../../src/assets/images/total-review.svg';

import {ReactComponent as EndToEndIconDisabled} from '../../../../../src/assets/images/end-disabled.svg';
import {ReactComponent as ReviewRateTrendIconDisabled} from '../../../../../src/assets/images/rate-disabled.svg';
import {ReactComponent as ReviewProductivityIconDisabled} from '../../../../../src/assets/images/productivity-disabled.svg';
import {ReactComponent as ReviewProgressIconDisabled} from '../../../../../src/assets/images/routereviewer-disabled.svg';
import {ReactComponent as TaggingIconDisabled} from '../../../../../src/assets/images/tagging-disabled.svg';
import {ReactComponent as TotalReviewIconDisabled} from '../../../../../src/assets/images/total-review-disabled.svg';

import {widgetIdMapping} from '../../dashboardUtils';
import {
  useDeleteWidget,
  useSaveWidgetData,
  useSaveWidgetSequence,
  useWidgetList,
} from '../../../../hooks/dashboard/useDashboard';

interface WidgetSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onFail: () => void;
  currentOrder: number[];
  currentSelected: number | null;
  existingWidgetIds: number[];
}

const widgets = [
  {
    id: widgetIdMapping.rateTrend,
    name: 'dashboard.reviewRateWidget.widgetTitle',
    icon: <ReviewRateTrendIcon />,
    disabledIcon: <ReviewRateTrendIconDisabled />,
  },
  {
    id: widgetIdMapping.endToEnd,
    name: 'dashboard.endToEndWidget.widgetSwitcherTitle',
    icon: <EndToEndIcon />,
    disabledIcon: <EndToEndIconDisabled />,
  },
  {
    id: widgetIdMapping.tagging,
    name: 'dashboard.taggingWidget.title',
    icon: <TaggingIcon />,
    disabledIcon: <TaggingIconDisabled />,
  },
  {
    id: widgetIdMapping.reviewerProgress,
    name: 'dashboard.reviewerWidget.widgetTitle',
    icon: <ReviewProgressIcon />,
    disabledIcon: <ReviewProgressIconDisabled />,
  },
  {
    id: widgetIdMapping.reviewerProductivity,
    name: 'dashboard.reviewerProductivity.widgetTitle',
    icon: <ReviewProductivityIcon />,
    disabledIcon: <ReviewProductivityIconDisabled />,
  },
  {
    id: widgetIdMapping.totalReviewProgress,
    name: 'dashboard.totalReview.title',
    icon: <TotalReviewIcon />,
    disabledIcon: <TotalReviewIconDisabled />,
  },
];

export const WidgetSwitcher = ({
  isOpen,
  onClose,
  currentOrder,
  currentSelected,
  onSuccess,
  onFail,
  existingWidgetIds,
}: WidgetSwitcherProps) => {
  const [selectedWidgetId, setSelectedWidgetId] = useState<number | null>(null);

  const {data: widgetList} = useWidgetList();
  const {mutate: saveWidgetData} = useSaveWidgetData();
  const {mutate: saveWidgetSequence} = useSaveWidgetSequence();
  const {mutate: deleteWidget} = useDeleteWidget();

  const handleClick = (id: number) => {
    setSelectedWidgetId(id);
  };

  const onCancelClick = () => {
    setSelectedWidgetId(null);
    onClose();
  };

  const swapWidget = () => {
    if (currentSelected && selectedWidgetId) {
      try {
        deleteWidget(currentSelected, {
          onSuccess: () => {
            saveWidgetData(
              {IsActive: true, WidgetID: selectedWidgetId},
              {
                onError: () => {
                  onFail();
                  onCancelClick();
                },
                onSuccess: () => {
                  const newOrder = [...currentOrder];
                  const indexToReplace = newOrder.indexOf(currentSelected);
                  newOrder[indexToReplace] = selectedWidgetId;
                  saveWidgetSequence(
                    {widgetIDList: newOrder},
                    {
                      onSuccess: () => {
                        onSuccess();
                        onCancelClick();
                      },
                      onError: () => {
                        onFail();
                        onCancelClick();
                      },
                    }
                  );
                },
              }
            );
          },
        });
      } catch (e) {
        console.log(e);
        onFail();
        onCancelClick();
      }
    }
  };
  return (
    <ArrModal
      showModal={isOpen}
      onClose={onCancelClick}
      headerTitleClassName="headline3_size"
      showClose={false}
      headerTitle={t('dashboard.widgetSwitcher.title')}
      className="widget-swap-modal"
      dataTestId="widget-swap-modal"
      buttons={[
        {
          id: '1',
          label: t('dashboard.widgetSwitcher.cancel'),
          onClick: onCancelClick,
          variant: 'secondary',
          size: 'small',
          dataTestId: 'ws-cancel-button',
        },
        {
          id: '2',
          label: t('dashboard.widgetSwitcher.save'),
          onClick: () => {
            swapWidget();
          },
          variant: 'primary',
          size: 'small',
          disabled: selectedWidgetId === null,
          dataTestId: 'ws-save-button',
        },
      ]}
    >
      <Box className="modal-container">
        <Typography
          className="typo-text subhead4_size text-color"
          data-testid="widget-switcher-desc"
        >
          {t('dashboard.widgetSwitcher.description')}
        </Typography>
        <Box className="widget-grid">
          {widgetList
            ? widgetList.map((widget) => {
                let widClass = '';
                const disabled =
                  existingWidgetIds?.includes(widget.widgetID) ?? false;
                if (selectedWidgetId === widget.widgetID) {
                  widClass = 'selected';
                } else if (selectedWidgetId !== null) {
                  widClass = 'unselected';
                }
                const wid = widgets.find((w) => w.id === widget.widgetID);
                return wid ? (
                  <Box
                    key={widget.widgetID}
                    data-testid={`widget-switcher-button-${widget.widgetID}`}
                    className={`${disabled ? 'widget-button-disabled' : 'widget-button'} ${widClass}`}
                    onClick={() => !disabled && handleClick(widget.widgetID)}
                  >
                    {disabled ? wid.disabledIcon : wid.icon}
                    <Typography
                      className={`typo-text subhead5_size ${disabled ? 'widget-button-color-disabled' : 'widget-button-color'}`}
                      data-testid={`widget-switcher-button-label-${widget.widgetID}`}
                    >
                      {t(wid.name)}
                    </Typography>
                  </Box>
                ) : null;
              })
            : null}
        </Box>
      </Box>
    </ArrModal>
  );
};
